/**
 * Executive Season Analytics — V1.0
 *
 * Cross-team organisation overview for senior staff.
 * Bound to: GET /api/executive-season-analytics/organization
 *
 * Sections:
 *  1  Executive KPIs (4 cards)
 *  2  Executive Narrative
 *  3  Team Comparison Table
 *  4  Availability Across Teams
 *  5  Injury Burden Across Teams
 *  6  Load Distribution Across Teams
 *  7  Wellness Comparison
 *  8  Performance Leaders (Top 5 per discipline)
 *  9  Cross-Team Rankings
 * 10  Key Findings
 * 11  Strategic Recommendations
 */
import React, { useEffect, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  Activity, AlertTriangle, Award, ChevronRight, Crown, Download,
  FileSpreadsheet, Heart, Loader2, ShieldAlert, Trophy, Users, Zap,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const PALETTE = ['#1E6BFF', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899'];

const KpiCard = ({ label, value, hint, accent = 'primary', testid }) => (
  <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">{label}</div>
    <div className={`text-3xl font-bold ${
      accent === 'good' ? 'text-emerald-400'
        : accent === 'warn' ? 'text-amber-400'
        : accent === 'critical' ? 'text-red-400'
        : 'text-cyan-300'
    }`}>{value}</div>
    {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
  </Card>
);

const SectionTitle = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-3">
    {Icon && <Icon className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />}
    <div>
      <h2 className="text-lg font-semibold text-zinc-100">{title}</h2>
      {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

const tooltipStyle = {
  backgroundColor: '#0f172a', border: '1px solid #334155',
  borderRadius: '6px', fontSize: '11px',
};

const ExecutiveSeasonAnalyticsPage = () => {
  const { api } = useAuth();
  const { language } = useLanguage();
  const t = (en, it) => (language === 'IT' ? it : en);

  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    const url = '/executive-season-analytics/organization' +
      (start && end ? `?start_date=${start}&end_date=${end}` : '');
    setLoading(true);
    setError(null);
    api.get(url)
      .then(r => {
        setData(r.data);
        if (!start) setStart(r.data.period.start_date);
        if (!end) setEnd(r.data.period.end_date);
      })
      .catch(e => setError(e?.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);

  const downloadBlob = async (path, body, filename, label) => {
    setExporting(label);
    try {
      const opts = body ? [path, body, { responseType: 'blob' }]
                          : [path, { responseType: 'blob' }];
      const res = body ? await api.post(...opts) : await api.get(...opts);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${label.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to export ${label}`);
    } finally { setExporting(null); }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24 text-zinc-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('Loading…', 'Caricamento…')}
      </div>
    );
  }
  if (error) return <div className="p-6 text-red-400">{error}</div>;
  if (!data) return null;

  const {
    organization, kpis, team_comparison: rows,
    availability_across_teams: availData,
    injury_burden_across_teams: injData,
    load_distribution_across_teams: loadData,
    wellness_comparison: wellData,
    performance_leaders: leaders,
    cross_team_rankings: rankings,
    narrative, findings, recommendations,
  } = data;

  return (
    <div className="space-y-6 fade-in" data-testid="executive-season-analytics-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Crown className="w-5 h-5 text-amber-400" />
            <span className="text-[11px] uppercase tracking-wider text-zinc-500">
              {t('Executive Suite', 'Suite Executive')}
            </span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100">
            {t('Executive Season Analytics', 'Analytics di Stagione Executive')}
            {organization?.name && (
              <span className="text-zinc-400 font-normal"> — {organization.name}</span>
            )}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {data.period.start_date} → {data.period.end_date}
            <span className="mx-2">·</span>
            {data.team_count} {t('teams', 'squadre')}
            <span className="mx-2">·</span>
            {kpis.total_athletes} {t('athletes', 'atleti')}
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block">{t('Start', 'Inizio')}</label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
              className="bg-zinc-900 border-zinc-700 text-zinc-100 h-9 w-40"
              data-testid="exec-start-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-zinc-500 block">{t('End', 'Fine')}</label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              className="bg-zinc-900 border-zinc-700 text-zinc-100 h-9 w-40"
              data-testid="exec-end-input" />
          </div>
          <Button onClick={() => downloadBlob(
            '/premium-reports/executive-season',
            { start_date: start, end_date: end },
            `executive_season_${organization?.id?.slice(0, 8)}.pdf`, 'pdf')}
            disabled={!!exporting}
            className="h-9 bg-amber-600 hover:bg-amber-500 text-white"
            data-testid="export-executive-pdf-btn">
            {exporting === 'pdf'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Download className="w-4 h-4 mr-2" />}
            {t('Executive PDF', 'PDF Executive')}
          </Button>
          <Button onClick={() => downloadBlob(
            '/executive-season-analytics/organization/excel' +
              (start && end ? `?start_date=${start}&end_date=${end}` : ''),
            null,
            `executive_season_${organization?.id?.slice(0, 8)}.xlsx`, 'xlsx')}
            disabled={!!exporting}
            variant="outline"
            className="h-9 border-zinc-700 text-zinc-200 hover:bg-zinc-800"
            data-testid="export-executive-xlsx-btn">
            {exporting === 'xlsx'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            {t('Excel', 'Excel')}
          </Button>
        </div>
      </div>

      {data.small_sample && (
        <div className="bg-amber-950/30 border border-amber-500/40 rounded p-3 text-xs text-amber-200 flex items-center gap-2"
          data-testid="exec-small-sample-banner">
          <AlertTriangle className="w-4 h-4" />
          <b>{t('Small Sample Size', 'Campione Ridotto')}</b> —
          {t(' Only 1 team in the organisation. Comparisons across teams not available.',
             ' Solo 1 squadra in organizzazione. Confronti tra squadre non disponibili.')}
        </div>
      )}

      {/* 1 — Executive KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="exec-kpis">
        <KpiCard label="Global Availability" value={`${kpis.availability_pct}%`}
          accent={kpis.availability_pct >= 90 ? 'good' : kpis.availability_pct >= 70 ? 'warn' : 'critical'}
          hint={`${kpis.total_athletes} athletes`}
          testid="exec-kpi-availability" />
        <KpiCard label="Total Training Load" value={`${kpis.total_load.toLocaleString()} AU`}
          hint={`${kpis.total_sessions.toLocaleString()} sessions`}
          testid="exec-kpi-load" />
        <KpiCard label="Global Wellness" value={kpis.wellness_index ? `${kpis.wellness_index}/10` : '—'}
          accent={kpis.wellness_index >= 7 ? 'good' : kpis.wellness_index >= 5.5 ? 'warn' : (kpis.wellness_index ? 'critical' : 'primary')}
          testid="exec-kpi-wellness" />
        <KpiCard label="Injury Burden Days" value={`${kpis.injury_burden_days}`}
          accent={kpis.injury_burden_days === 0 ? 'good' : kpis.injury_burden_days < 200 ? 'warn' : 'critical'}
          hint={`${kpis.total_injuries} injuries`}
          testid="exec-kpi-injury-burden" />
      </div>

      {/* 2 — Narrative */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="exec-narrative">
        <SectionTitle icon={Award} title={t('Executive Summary', 'Riassunto Executive')}
          subtitle={t('Auto-generated · deterministic', 'Generato automaticamente')} />
        <p className="text-sm text-zinc-300 leading-relaxed">{narrative}</p>
      </Card>

      {/* 3 — Team Comparison */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="exec-team-comparison">
        <SectionTitle icon={Users} title={t('Team Comparison', 'Confronto Squadre')} />
        {rows?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-zinc-500 border-b border-zinc-800">
                  <th className="text-left py-2 pr-3">Team</th>
                  <th className="text-left pr-3">Category</th>
                  <th className="text-right pr-3">Squad</th>
                  <th className="text-right pr-3">Avail %</th>
                  <th className="text-right pr-3">Load</th>
                  <th className="text-right pr-3">Wellness</th>
                  <th className="text-right pr-3">Injuries</th>
                  <th className="text-right">Days Lost</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.team_id} className="border-b border-zinc-800/60 last:border-0">
                    <td className="py-2 pr-3 text-zinc-100 font-medium">{r.name}</td>
                    <td className="pr-3 text-zinc-400">{r.category || '—'}</td>
                    <td className="text-right pr-3 text-zinc-300 tabular-nums">{r.squad_size}</td>
                    <td className="text-right pr-3 text-zinc-100 tabular-nums">{r.availability_pct}%</td>
                    <td className="text-right pr-3 text-zinc-300 tabular-nums">{r.total_load.toLocaleString()}</td>
                    <td className="text-right pr-3 text-zinc-300 tabular-nums">{r.wellness_index || '—'}</td>
                    <td className="text-right pr-3 text-zinc-300 tabular-nums">{r.injury_count}</td>
                    <td className="text-right text-amber-300 tabular-nums">{r.injury_burden_days}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="text-xs text-zinc-500 italic py-4">No team data available.</div>}
      </Card>

      {/* 4 — Availability Across Teams */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="exec-availability">
        <SectionTitle icon={Activity} title={t('Availability Across Teams', 'Disponibilità per Squadra')} />
        {availData?.length ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={availData} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
              <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 10 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="availability_pct" fill="#22C55E" />
            </BarChart>
          </ResponsiveContainer>
        ) : <div className="text-xs text-zinc-500 italic">—</div>}
      </Card>

      {/* 5 — Injury Burden */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="exec-injury-burden">
        <SectionTitle icon={ShieldAlert} title={t('Injury Burden Across Teams', 'Carico Infortuni per Squadra')} />
        {injData?.length ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={injData} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
              <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="injury_burden_days" fill="#EF4444" name="Days Lost" />
            </BarChart>
          </ResponsiveContainer>
        ) : <div className="text-xs text-zinc-500 italic">—</div>}
      </Card>

      {/* 6 — Load Distribution */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="exec-load-distribution">
        <SectionTitle icon={Zap} title={t('Load Distribution Across Teams', 'Distribuzione Carico per Squadra')} />
        {loadData?.length && loadData.some(r => r.total_load) ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={loadData} dataKey="total_load" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {loadData.map((d, i) => <Cell key={d.name} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="text-xs space-y-2">
              {loadData.map((r, i) => (
                <div key={r.team_id} className="flex justify-between border-b border-zinc-800/60 pb-1.5">
                  <span className="text-zinc-400 flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
                    {r.name}
                  </span>
                  <span className="text-zinc-100 tabular-nums">{r.total_load.toLocaleString()} · {r.share_pct}%</span>
                </div>
              ))}
            </div>
          </div>
        ) : <div className="text-xs text-zinc-500 italic">—</div>}
      </Card>

      {/* 7 — Wellness Comparison */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="exec-wellness">
        <SectionTitle icon={Heart} title={t('Wellness Comparison', 'Confronto Benessere')} />
        {wellData?.length && wellData.some(r => r.wellness_index) ? (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={wellData} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
              <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis domain={[0, 10]} tick={{ fill: '#94a3b8', fontSize: 10 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="wellness_index" fill="#1E6BFF" />
            </BarChart>
          </ResponsiveContainer>
        ) : <div className="text-xs text-zinc-500 italic">—</div>}
      </Card>

      {/* 8 — Performance Leaders */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="exec-performance-leaders">
        {[
          { title: t('Top — Jump (cm)', 'Top — Salto (cm)'), key: 'top_jump', suffix: ' cm' },
          { title: t('Top — Peak Power (kW)', 'Top — Potenza (kW)'), key: 'top_power', suffix: ' kW' },
          { title: t('Top — Availability (%)', 'Top — Disponibilità (%)'), key: 'top_availability', suffix: '%' },
          { title: t('Top — MAS (km/h)', 'Top — MAS (km/h)'), key: 'top_mas', suffix: ' km/h' },
          { title: t('Top — VO\u2082 (ml/kg/min)', 'Top — VO\u2082 (ml/kg/min)'), key: 'top_vo2', suffix: '' },
        ].map(card => (
          <Card key={card.key} className="bg-zinc-900 border-zinc-800 p-4">
            <div className="text-xs uppercase text-zinc-500 mb-3 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-400" />{card.title}
            </div>
            {leaders?.[card.key]?.length ? (
              <div className="space-y-1.5">
                {leaders[card.key].slice(0, 5).map((r, i) => (
                  <div key={r.athlete_id} className="flex justify-between text-xs py-1 border-b border-zinc-800/60 last:border-0">
                    <span className="text-zinc-300">{i + 1}. {r.name} <span className="text-zinc-500">· {r.team}</span></span>
                    <span className="text-zinc-100 font-semibold tabular-nums">{r.value}{card.suffix}</span>
                  </div>
                ))}
              </div>
            ) : <div className="text-xs text-zinc-500 italic py-2">No data.</div>}
          </Card>
        ))}
      </div>

      {/* 9 — Cross-Team Rankings */}
      <div data-testid="exec-cross-team-rankings">
        <SectionTitle icon={Award} title={t('Cross-Team Rankings', 'Classifiche Cross-Squadra')} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { title: 'Best Availability', key: 'best_availability', getVal: r => `${r.availability_pct}%`, accent: 'text-emerald-400' },
            { title: 'Worst Availability', key: 'worst_availability', getVal: r => `${r.availability_pct}%`, accent: 'text-red-400' },
            { title: 'Highest Load', key: 'highest_load', getVal: r => `${r.total_load.toLocaleString()} AU`, accent: 'text-cyan-300' },
            { title: 'Lowest Injury Burden', key: 'lowest_injury_burden', getVal: r => `${r.injury_burden_days} d`, accent: 'text-emerald-400' },
            { title: 'Highest Injury Burden', key: 'highest_injury_burden', getVal: r => `${r.injury_burden_days} d`, accent: 'text-red-400' },
            { title: 'Best Wellness', key: 'best_wellness', getVal: r => `${r.wellness_index || '—'}`, accent: 'text-emerald-400' },
          ].map(card => (
            <Card key={card.key} className="bg-zinc-900 border-zinc-800 p-4">
              <div className="text-xs uppercase text-zinc-500 mb-3">{card.title}</div>
              {rankings?.[card.key]?.length ? (
                <div className="space-y-1.5">
                  {rankings[card.key].map((r, i) => (
                    <div key={r.team_id || i} className="flex justify-between text-xs py-1 border-b border-zinc-800/60 last:border-0">
                      <span className="text-zinc-300">{i + 1}. {r.name}</span>
                      <span className={`font-semibold tabular-nums ${card.accent}`}>{card.getVal(r)}</span>
                    </div>
                  ))}
                </div>
              ) : <div className="text-xs text-zinc-500 italic py-2">—</div>}
            </Card>
          ))}
        </div>
      </div>

      {/* 10 + 11 — Findings + Strategic Recommendations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="exec-findings">
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <SectionTitle icon={Award} title={t('Key Findings', 'Risultati Chiave')} />
          <ul className="space-y-2 text-sm text-zinc-300">
            {findings.map((f, i) => <li key={i}>• {f}</li>)}
          </ul>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <SectionTitle icon={ChevronRight} title={t('Strategic Recommendations', 'Raccomandazioni Strategiche')} />
          <ul className="space-y-2 text-sm text-zinc-300">
            {recommendations.map((r, i) => <li key={i}>• {r}</li>)}
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default ExecutiveSeasonAnalyticsPage;
